import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import MiniTaskGameHeader from '../components/MiniTaskGameHeader';
import { GardenBackdrop } from '../components/RoundView';
import { withTurkishGenitive } from '../turkishSuffixes';
import { recoverSoundDiscoveryRound, saveSoundDiscoveryProgress } from '../miniTasksApi';
import SchoolN from './SchoolN';
import { setRoundAudioPlayback } from './pronunciationPlayback';
import './soundHunt.css';
import './soundDiscovery.css';

const STEPS = [
  ['i_l1_g3_instruction', 'learning_started', null],
  ['w_l1_g3_nar', 'word_presented', 'nar'], ['w_l1_g3_inek', 'word_presented', 'inek'],
  ['w_l1_g3_limon', 'word_presented', 'limon'], ['i_l1_g3_compare', 'comparison_started', null],
  ['w_l1_g3_nar', 'word_presented', 'nar'], ['w_l1_g3_inek', 'word_presented', 'inek'],
  ['w_l1_g3_limon', 'word_presented', 'limon'], ['f_l1_g3_discovery', 'discovery_completed', null],
];

export default function SoundDiscoveryRoundView({ round, boardSessionId, classTitle, soundEnabled = true,
  onToggleSound, onComplete, onExit, sharedAudioRef, onNextRound }) {
  const [phase, setPhase] = useState('loading');
  const [speaking, setSpeaking] = useState(null);
  const [discovered, setDiscovered] = useState(false);
  const [stage, setStage] = useState(0);
  const [error, setError] = useState('');
  const localAudioRef = useRef(null);
  const audioRef = sharedAudioRef || localAudioRef;
  const muted = useRef(!soundEnabled);
  muted.current = !soundEnabled;
  const complete = useRef(onComplete);
  complete.current = onComplete;
  const handlers = useRef({});
  useEffect(() => { if (audioRef.current) audioRef.current.muted = !soundEnabled; }, [audioRef, soundEnabled]);

  useEffect(() => {
    let alive = true, running = false, cancelAudio, retry, current = round, events = [], origin = 0, prepared = false;
    const elapsed = () => Math.max(events.at(-1)?.elapsed_ms || 0, Date.now() - origin);
    const append = (kind, item_id = null) => {
      events = [...events, { kind, item_id, elapsed_ms: kind === 'learning_started' ? 0 : elapsed() }];
    };
    const teachingDone = () => events.some((event) => event.kind === 'discovery_completed');
    const play = (id, item = null) => new Promise((resolve, reject) => {
      const audio = audioRef.current || new Audio(round.audio[id]);
      audioRef.current = audio;
      audio.src = round.audio[id]; audio.preload = 'auto'; audio.muted = muted.current;
      setRoundAudioPlayback(audio, round, id);
      setSpeaking(item);
      const clean = () => { clearTimeout(timer); audio.onended = null; audio.onerror = null; cancelAudio = null;
        if (alive) setSpeaking(null); };
      const fail = (err) => { clean(); audio.pause(); reject(err); };
      const timer = setTimeout(() => fail(new Error('Ses yüklenemedi. Yeniden deneyelim.')), 30000);
      audio.onended = () => { clean(); resolve(); };
      audio.onerror = () => fail(new Error('Ses yüklenemedi. Yeniden deneyelim.'));
      cancelAudio = () => fail(new Error('cancelled'));
      try { Promise.resolve(audio.play()).catch(fail); } catch (err) { fail(err); }
    });
    const save = async () => {
      if (!round.preview) return saveSoundDiscoveryProgress(round.round_id, boardSessionId, events);
      const finished = events.some((event) => event.kind === 'completed');
      return { round_completed: finished, summary: { preview: true, task_type: 'sound_discovery', completed: finished,
        target_sound: 'n', measurement_type: 'LEARNING / PRACTICE',
        completion_time_ms: finished ? events.at(-1).elapsed_ms : null,
        replay_instruction_count: events.filter((event) => event.kind === 'instruction_replay').length,
        free_replay_count: events.filter((event) => event.kind === 'free_replay').length, rounds_remaining: 1 },
        next_round: finished ? round.next_round : null };
    };
    const ready = () => { if (alive) setPhase('ready'); };
    const run = async (operation) => {
      if (running || !alive) return;
      running = true; setPhase('playing'); setError(''); retry = () => run(operation);
      try { await operation(); }
      catch (err) { if (alive) { setError(err.name === 'NotAllowedError'
        ? 'Ses başlayamadı. Yönergeyi dinleyelim düğmesine yeniden dokun.' : err.message); setPhase('retry'); } }
      finally { running = false; }
    };
    const teach = () => {
      if (running || !alive) return;
      if (current.status === 'completed') { finish(); return; }
      const replay = teachingDone();
      let cursor = replay ? 0 : events.length, stepDone = false, recordedReplay = false;
      if (!events.length) origin = Date.now();
      if (replay) { setDiscovered(false); setStage(0); }
      run(async () => {
        while (cursor < STEPS.length) {
          const [clip, kind, item] = STEPS[cursor];
          if (cursor >= 8) setDiscovered(true);
          if (!stepDone) {
            await play(clip, item);
            if (!alive) return;
            if (!replay) append(kind, item);
            stepDone = true;
          }
          if (!replay) await save();
          if (!alive) return;
          cursor += 1; stepDone = false;
          setStage(cursor >= 9 ? 3 : cursor >= 8 ? 2 : cursor >= 4 ? 1 : 0);
        }
        if (replay) {
          if (!recordedReplay) { append('instruction_replay'); recordedReplay = true; }
          await save();
        }
        setDiscovered(true); ready();
      });
    };
    const freeReplay = (item) => {
      if (running || !teachingDone()) return;
      let recorded = false;
      run(async () => {
        if (!recorded) {
          await play(item.audio_id, item.id);
          if (!alive) return;
          append('free_replay', item.id); recorded = true;
        }
        await save(); ready();
      });
    };
    const finish = () => {
      if (running || (!teachingDone() && current.status !== 'completed')) return;
      let result, saved = false, spoken = false;
      const clip = onNextRound && (current.next_task_available || current.next_round)
        ? 'i_l1_g4_continue' : 'f_l1_g3_complete';
      run(async () => {
        if (!events.some((event) => event.kind === 'completed')) append('completed');
        if (!saved && !spoken) {
          // Start audio in the button's gesture, including a cold completed restore.
          const [recording, playback] = await Promise.allSettled([save(), play(clip)]);
          if (recording.status === 'fulfilled') { result = recording.value; saved = true; }
          if (playback.status === 'fulfilled') spoken = true;
          if (recording.status === 'rejected') throw recording.reason;
          if (playback.status === 'rejected') throw playback.reason;
        } else {
          if (!saved) { result = await save(); saved = true; }
          if (!spoken) { await play(clip); spoken = true; }
        }
        if (!alive) return;
        const next = result.next_round || current.next_round;
        if (next && onNextRound) onNextRound({ ...next, intro_bridge_played: true });
        else complete.current(result.summary);
      });
    };
    handlers.current = { teach, freeReplay, finish, retry: () => retry?.() };
    run(async () => {
      if (!round.preview) current = await recoverSoundDiscoveryRound(round.round_id, boardSessionId);
      if (!alive) return;
      events = current.progress?.events || [];
      origin = Date.now() - (events.at(-1)?.elapsed_ms || 0);
      const count = Math.min(events.length, STEPS.length);
      setStage(count >= 9 ? 3 : count >= 8 ? 2 : count >= 4 ? 1 : 0);
      setDiscovered(count >= 8);
      prepared = true;
      setPhase(teachingDone() && current.status !== 'completed' ? 'ready' : 'waiting');
    }).then(() => {
      if (alive && prepared && round.continuing && (!teachingDone() || current.status === 'completed')) teach();
    });
    return () => { alive = false; cancelAudio?.(); audioRef.current?.pause(); };
  }, [round, boardSessionId, audioRef, onNextRound]);

  return <Box className="sound-hunt sound-discovery">
    <GardenBackdrop />
    <Box className="sound-hunt-content">
      <MiniTaskGameHeader classTitle={classTitle} soundEnabled={soundEnabled} onToggleSound={onToggleSound} />
      <Box className="sound-hunt-header">
        <Button className="sound-hunt-home" onClick={onExit} startIcon={<ArrowBackRoundedIcon />}>Ana sayfa</Button>
        <Typography component="h1" className="sound-hunt-title">{withTurkishGenitive(round.student?.display_name || 'Öğrenci')} Mini Görevi</Typography>
        <Box className="sound-hunt-progress" aria-label={`${stage} dinleme aşaması tamamlandı`}>
          {[0, 1, 2].map((i) => <span key={i} className={i < stage ? 'found' : ''}>●</span>)}
        </Box>
      </Box>
      <Box className="sound-hunt-bottom">
        <Typography className="sound-hunt-badge">⭐ N Sesini Keşfet</Typography>
        <Box className="sound-discovery-glyph" aria-label={discovered ? 'Yeni sesin harfi' : undefined}>
          {discovered && <SchoolN />}
        </Box>
        <Box className="sound-hunt-cards">
          {round.items.map((item, i) => <Box key={item.id} role="group" aria-label={`${i + 1}. dinleme kartı`}
            className={`sound-hunt-card ${speaking === item.id ? 'is-speaking' : ''}`}>
            <button type="button" className="sound-hunt-picture" disabled={phase !== 'ready'}
              aria-label={`${i + 1}. resmi dinle`} onClick={() => handlers.current.freeReplay(item)}>
              <img src={item.image.url} alt="" draggable="false" />
              <span className="sound-discovery-speaker" aria-hidden="true"><VolumeUpRoundedIcon /></span>
            </button>
          </Box>)}
        </Box>
        <Box className="sound-hunt-controls">
          <Button className="sound-hunt-control" startIcon={<RecordVoiceOverRoundedIcon />}
            disabled={!['waiting', 'ready', 'retry'].includes(phase)}
            onClick={() => phase === 'retry' ? handlers.current.retry() : handlers.current.teach()}>Yönergeyi dinleyelim</Button>
          {discovered && <Button className="sound-hunt-control sound-discovery-finish" startIcon={<CheckRoundedIcon />}
            disabled={phase !== 'ready'} onClick={() => handlers.current.finish()}>
            {onNextRound && (round.next_task_available || round.next_round) ? 'Devam et' : 'Tamamla'}</Button>}
        </Box>
        <Typography role="status" className="sound-hunt-status">{error}</Typography>
      </Box>
    </Box>
  </Box>;
}
